import type { MovementPattern } from '@heraion/shared';

// Single source for mapping a catalog Exercise to the DraftEntry logging-kind
// flags. Every entry-create/replace path — add_exercise (useAddExercise),
// plan-starter hydrate (planStarterHydration), and exercise swap
// (useReplaceExercise) — derives them the SAME way, so they can't
// disagree on how an exercise's catalog row becomes isCardio/isTimeBased/
// isBodyweight. A divergence caused IB-2: the swap ignored isTimeBased, so an
// isometric hold logged as reps×weight instead of a timed hold.
//
// The param is loose (all fields optional) so callers with a possibly-missing
// catalog row (plan-starter) can pass `ex?.field` and a pre-resolved
// movementPattern fallback; callers with a definite Exercise pass it whole.
type EntryKindFlags = {
  isBodyweight: boolean;
  isCardio: boolean;
  isTimeBased: boolean;
  movementPattern: MovementPattern | null;
};

export function deriveEntryKind(ex: {
  isBodyweight?: boolean | null;
  movementPattern?: MovementPattern | null;
  isTimeBased?: boolean | null;
}): EntryKindFlags {
  const movementPattern = ex.movementPattern ?? null;
  return {
    isBodyweight: ex.isBodyweight === true,
    isCardio: movementPattern === 'cardio',
    isTimeBased: ex.isTimeBased === true,
    movementPattern,
  };
}

// Laterality seed for an entry — the catalog `unilateral` flag drives the
// ×2 multiplier, cardio never doubles. Extracted here first (IB-8, inbox
// 5aa7017d: the swap never seeded it), then lifted to shared setVolume.ts
// in Slice 2 so the API's save-time stamp (setFactory.buildLogSet) can't
// diverge from the logger's seed. Re-exported so the logger callsites
// (useAddExercise / planStarterHydration / useReplaceExercise) keep importing
// their entry-derivation rules from this one file.
export { catalogLoadedSides } from '@heraion/shared';

// The primary logged metric splits kinds into two families: timed/cardio log a
// DURATION, everything else logs REPS. A swap that crosses this line (IB-6)
// orphans the set's already-typed field — a completed weighted set (reps) swept
// to a timed exercise (duration) would be silently dropped on save — so the
// swap path warns + resets the sets instead of losing them quietly.
export function kindNeedsDuration(f: {
  isCardio: boolean;
  isTimeBased: boolean;
}): boolean {
  return f.isCardio || f.isTimeBased;
}

// ...and the SET-level question, which is a different one (HI-1). The exercise
// answers "do I always log a duration"; a plan that bounds one set in time
// makes THAT set log one whatever the exercise is — a back squat prescribed
// 30s is still a reps-logging lift, and it still needs the clock, the timer
// column and the duration wording for that one set.
//
// It lives here, next to its sibling, because two surfaces ask it and two
// inline copies would drift the day the shape of a prescription changes: the
// start affordance (timerStartHandlers) and the effort meter's variant switch
// (FocusPanel's `isDuration`). Both used to ask `kindNeedsDuration` alone,
// which is why the countdown was offered on a strength bout while the meter
// had nowhere to render it.
export function setNeedsDuration(
  entry: { isCardio: boolean; isTimeBased: boolean },
  set: { workPrescribed?: unknown } | null | undefined,
): boolean {
  return kindNeedsDuration(entry) || set?.workPrescribed !== undefined;
}

/** Is this set a bout the plan runs on a clock — the one `SetWork` kind that
 *  ENDS by itself? Three callsites read it and they must not drift: whether
 *  the clock is allowed to overrun its target (useHoldTimer, and the cue it
 *  schedules — "your set is logged" rather than "tap to log it"), whether zero
 *  completes the set (useWorkBoutAutoComplete), and the word that names the
 *  result once it is ("done", not "held" — FocusPanelEffort). They agreed as
 *  three inline copies too; the day `SetWork` grows a third kind, two would be
 *  updated and one forgotten, and the forgotten one speaks to an athlete with
 *  the phone in a pocket.
 *
 *  ⚠️ Deliberately NARROWER than setNeedsDuration above: a `countup` bout logs
 *  a duration (so it gets the clock) but the athlete's tap ends it, so it is
 *  neither auto-completed nor announced as done-by-itself. */
export function isPrescribedBoutSet(
  set: { workPrescribed?: { kind: string } } | null | undefined,
): boolean {
  return set?.workPrescribed?.kind === 'countdown';
}
